import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';

import { useScrollDirection, ScrollDirection } from 'modules/common/hooks/useScrollDirection';

export const ANIMATION_DURATION = 400;
export const ANIMATION_EASING = 'cubic-bezier(0.2, 0.0, 0, 1.0)';
export const TRANSITION_DELAY = ANIMATION_DURATION - 100;
const bottomPadding = 5;

export default function useCompressAnimation(
    collapsibleSectionRef: React.RefObject<HTMLDivElement> | null,
    containerRef: React.RefObject<HTMLDivElement> | null,
    offsetRef?: React.RefObject<HTMLDivElement> | null,
    isForcedCompressed?: boolean
) {
    const [isCompressed, setIsCompressed] = useState(false);
    const { scrollDirection, lockScrollDirection } = useScrollDirection(containerRef, 100);
    const originalHeightRef = useRef(0);

    const applyStyle = useCallback(
        (style: 'compressed' | 'expanded') => {
            const collapsibleSection = collapsibleSectionRef?.current;
            if (collapsibleSection) {
                if (style === 'compressed') {
                    collapsibleSection.style.height = '0px';
                    collapsibleSection.style.overflow = 'hidden';
                    collapsibleSection.style.paddingBottom = '0px';
                } else {
                    collapsibleSection.style.height = `${originalHeightRef.current}px`;
                    collapsibleSection.style.overflow = 'visible';
                    collapsibleSection.style.paddingBottom = `${bottomPadding}px`;
                }
            }
        },
        [collapsibleSectionRef, originalHeightRef]
    );

    const updateOriginalHeight = useCallback(() => {
        const collapsibleSection = collapsibleSectionRef?.current;
        if (collapsibleSection) {
            const childNode = collapsibleSection.firstElementChild as HTMLElement;
            const collapsibleSectionHeight = childNode.getBoundingClientRect().height + bottomPadding;
            originalHeightRef.current = collapsibleSectionHeight;
            applyStyle(isCompressed ? 'compressed' : 'expanded');
        }
    }, [collapsibleSectionRef, applyStyle, isCompressed]);

    useLayoutEffect(() => {
        updateOriginalHeight();
    }, [updateOriginalHeight]);

    useEffect(() => {
        const collapsibleSection = collapsibleSectionRef?.current;
        if (collapsibleSection) {
            const resizeObserver = new ResizeObserver(() => {
                updateOriginalHeight();
            });
            // Observe changes on the collapsible section content to update the height when hiding/showing child elements
            resizeObserver.observe(collapsibleSection.firstElementChild as HTMLElement);

            return () => {
                resizeObserver.disconnect();
            };
        }
        return () => {};
    }, [collapsibleSectionRef, updateOriginalHeight]);

    useLayoutEffect(() => {
        const collapsibleSection = collapsibleSectionRef?.current;
        const container = containerRef?.current;
        const offset = offsetRef?.current;

        if (isForcedCompressed) {
            setIsCompressed(true);
            applyStyle('compressed');
            return;
        }

        if (collapsibleSection && container && offset) {
            // If header is set to compressed, do not animate
            offset.style.transition = `margin-top ${ANIMATION_DURATION}ms ${ANIMATION_EASING}`;

            // Animate the collapsible section when the scroll direction changes
            if (scrollDirection === ScrollDirection.down && !isCompressed) {
                // Compensate the header height change by adding margin to the container so there's no scroll difference
                offset.style.marginTop = `${originalHeightRef.current}px`;
                lockScrollDirection(ANIMATION_DURATION);
                setIsCompressed(true);
                applyStyle('compressed');
            } else if (scrollDirection === ScrollDirection.up && isCompressed) {
                // Compensate the header height change by removing margin from the container so there's no scroll difference
                offset.style.marginTop = `0px`;
                lockScrollDirection(ANIMATION_DURATION);
                setIsCompressed(false);
                applyStyle('expanded');
            }
        }
    }, [
        originalHeightRef,
        collapsibleSectionRef,
        containerRef,
        offsetRef,
        scrollDirection,
        isCompressed,
        isForcedCompressed,
        applyStyle,
        lockScrollDirection,
    ]);
    return { isCompressed };
}
